import React, { PureComponent, ReactElement } from 'react';
import classNames from 'classnames';
import PageTemplate from 'components/pageTemplate/PageTemplate';
import PaymentCardContainer from 'components/paymentCard/PaymentCardContainer';
import SavedEntitiesContainer from 'components/savedEntities';
import TopPanelContainer from 'components/topPanel/TopPanelContainer';
import DataListContainer from 'components/dataListContainer';
import GenerateTestPaymentsButton from './components/GenerateTestPaymentsButton';

import { getPayments } from 'api/payments';
import MetricService from 'helpers/metricService/MetricService';

import {
  minPaymentTableColumnsWidth,
  refundablePaymentStatuses,
  refundablePaymentTypes,
} from './constants';
import tableNames from 'constants/tableNames';
import savedEntities from 'constants/savedEntities';
import quickFiltersConfig from 'pages/payments/quickFiltersConfig';
import { Column, Header, Row } from 'components/ui/table';
import ItemConfiguration from 'types/ItemConfiguration';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import Lock from 'components/tableHeaders/components/lock/Lock';
import PaymentActions from 'components/paymentCard/paymentActions';
import setNoChangeWordOrder from 'helpers/setNoChangeWordOrder';
import SortingCell from 'components/tableHeaders/components/sortingCell/SortingCell';
import RoundButton from 'components/ui/roundButton';
import './payments.scss';
import SummaryView from './components/summary';
import { SortingType } from '../../types/sortings';
import { changeDataForceUpdate, resetData } from '../../actions/getData';
import { connect } from 'react-redux';
import { StoreProps } from '../../store';

interface OwnProps {
  id?: string;
  isDemoUser: boolean;
  configuration: ItemConfiguration[];
  feature_configuration: ItemConfiguration[];
  sort: {
    field: string;
    order: SortingType;
  };
  goToPayment: (data) => void;
  openTabWithPayment: (data) => void;
  isRefundEnabled: boolean;
}

type Props = OwnProps & IntlProps & StoreProps;

class PaymentsPage extends PureComponent<Props> {
  private readonly tableName = tableNames.payments;

  render() {
    return (
      <PageTemplate.Main>
        <PageTemplate.Entities>
          <SavedEntitiesContainer entityKey={savedEntities.payments} />
        </PageTemplate.Entities>
        {this.renderContent()}
      </PageTemplate.Main>
    );
  }

  renderContent() {
    const { id, isDemoUser, feature_configuration } = this.props;

    if (id) {
      return (
        <PaymentCardContainer id={id} savedEntity={savedEntities.payments} />
      );
    }
    return (
      <PageTemplate.Container>
        <TopPanelContainer
          name={this.tableName}
          filtersConfig={quickFiltersConfig}
          onResetCallback={() => {
            MetricService.send({
              action: 'click',
              actionKey: 'payments.registry.filterDeactivation',
            });
          }}
          isFeatureConfigurationButton={true}
          button={<GenerateTestPaymentsButton isDemoUser={isDemoUser} />}
          showRefreshButton={true}
          refreshButtonAction={() => {
            this.refreshData();
          }}
        />
        <DataListContainer
          tableName={this.tableName}
          apiRequest={getPayments}
          minColumnsWidth={minPaymentTableColumnsWidth}
          rowRender={this.rowRender}
          headerRender={this.headerRender()}
        />
        <div className='page-payments__action-panel'></div>
        {feature_configuration.find((item) => item.id === 'summary_table')
          ?.active === true && <SummaryView />}
      </PageTemplate.Container>
    );
  }

  headerRender = () => {
    return (
      <Header>
        {this.props.configuration.map((config) => {
          let content: string | ReactElement = this.props.getTranslate(
            config.localId
          );
          const isFixed = Boolean(config.fixed);

          if (config.id === 'paymentId') {
            content = (
              <Lock
                content={this.props.getTranslate(config.localId)}
                tableName={tableNames.payments}
                isFixed={isFixed}
              />
            );
          } else if (
            config.id === 'creationDate' ||
            config.id === 'updatedDate'
          ) {
            content = (
              <SortingCell
                tableName={tableNames.payments}
                id={config.id}
                sort={this.props.sort}>
                {content}
              </SortingCell>
            );
          }

          return (
            <Column
              id={config.id}
              key={config.id}
              customClass={isFixed ? 'ui-table__column_fixed' : ''}
              modifier={config.id}
              content={content}
              minColumnsWidth={minPaymentTableColumnsWidth}
            />
          );
        })}
      </Header>
    );
  };

  rowRender = (data) => {
    return (
      <Row
        onClick={() => data.handleDoubleClick(this.props.goToPayment)}
        key={data.transactionId}
        customClass='ui-table__row_clickable'>
        {this.props.configuration.map((config) => {
          if (config.id === 'paymentId') {
            const tooltip = this.getTooltip(data);
            return (
              <Column
                id={config.id}
                key={`data-${data.projectId}-${data.transactionId}-${config.id}`}
                customClass={classNames('page-payments__id', {
                  'ui-table__column_fixed': config.fixed,
                  'page-payments__id_no-action': !this.props.isRefundEnabled,
                })}
                modifier={config.id}
                content={
                  <>
                    <span className='ui-table__payment-id-and-button-open-in-new-tab'>
                      <RoundButton
                        status='light'
                        customClass='page-payments__button-open-in-new-tab'
                        icon='im-Tab-Open'
                        iconSize={11}
                        tooltip={this.props.getTranslate(
                          `payments.registry.openTab.button`
                        )}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          this.props.openTabWithPayment(data);
                          MetricService.send({
                            action: 'click',
                            actionKey: 'payments.registry.openTab',
                          });
                        }}
                      />
                      {setNoChangeWordOrder(data[config.id])}
                    </span>
                    {this.props.isRefundEnabled && (
                      <PaymentActions
                        transactionId={data.transactionId}
                        projectId={data.projectId}
                        buttonTheme='primary'
                        actionTypes={['refund']}
                        customClass='page-payments__action payment-actions_registry'
                        isNeedPing={false}
                        disabled={!this.isRefundEnabled(data)}
                        refundTooltip={tooltip}
                        startPingOnClick
                        buttonType='round'
                        refundMetric='payments.paymentRegistry.refund'
                        selectorPortal='.page-payments__action-panel'
                      />
                    )}
                  </>
                }
                minColumnsWidth={minPaymentTableColumnsWidth}
                columnWidths={data.columnWidths}
                data={data}
              />
            );
          }

          return (
            <Column
              id={config.id}
              key={`data-${data.projectId}-${data.transactionId}-${config.id}`}
              modifier={config.id}
              content={data[config.id] || '—'}
              minColumnsWidth={minPaymentTableColumnsWidth}
              columnWidths={data.columnWidths}
              data={data}
              params={{
                ...config,
              }}
            />
          );
        })}
      </Row>
    );
  };

  getTooltip = (data) => {
    if (this.isRefundEnabled(data)) {
      if (data.paymentType === 'recurring') {
        return 'payments.registry.refund.recurring.popup';
      }
      return 'payments.registry.refund.popup';
    }

    if (!refundablePaymentTypes.includes(data.paymentType)) {
      return 'payments.single.reason.invalidType.popup';
    }
    if (!refundablePaymentStatuses.includes(data.paymentStatus)) {
      return 'payments.single.reason.invalidStatus.popup';
    }
  };

  isRefundEnabled = (data) => {
    return (
      refundablePaymentTypes.includes(data.paymentType) &&
      refundablePaymentStatuses.includes(data.paymentStatus)
    );
  };

  refreshData = () => {
    const { dispatch } = this.props;
    dispatch(resetData(this.tableName));
    dispatch(changeDataForceUpdate(this.tableName, true));
  };
}

export default connect()(addTranslation(PaymentsPage));
